/**
 * EltJobDetailsModal Component
 *
 * Modal for viewing ELT job details.
 */

import React from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import { Alert } from '../../../components/ui/Alert';
import { useEltJobDetails } from '../hooks/useEltJobDetails';
import { useManagedSqlDbs } from '../../managed-sql-databases/hooks/useManagedSqlDbs';
import type { EltJob } from '../../../types/elt-job.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface EltJobDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  eltJobId: string;
  onTrigger: (job: EltJob) => void;
}

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">{children}</dd>
  </div>
);

export const EltJobDetailsModal: React.FC<EltJobDetailsModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  eltJobId,
  onTrigger,
}) => {
  const { data: job, isLoading, error } = useEltJobDetails(tenantId, eltJobId);
  const { data: managedDbsData } = useManagedSqlDbs(tenantId);

  // Find the target database by ID
  const targetDatabase = job?.managedSqlDbId
    ? managedDbsData?.find((db) => db.managedSqlDbId === job.managedSqlDbId)
    : null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <Modal.Header>ELT Job Details</Modal.Header>
      <Modal.Body>
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner size="lg" />
          </div>
        )}

        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {job && (
          <div className="space-y-6">
            {/* Basic Info */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Basic Information</h3>
              <dl className="border-t border-gray-200">
                <DetailRow label="Name">{job.eltJobName}</DetailRow>
                <DetailRow label="Code">
                  <span className="font-mono text-sm">{job.eltJobCode}</span>
                </DetailRow>
                <DetailRow label="Description">
                  {job.description || <span className="text-gray-400">No description</span>}
                </DetailRow>
                <DetailRow label="Active">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      job.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {job.isActive ? 'Yes' : 'No'}
                  </span>
                </DetailRow>
              </dl>
            </div>

            {/* Configuration */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Configuration</h3>
              <dl className="border-t border-gray-200">
                <DetailRow label="Schedule">
                  {job.cronExpression ? (
                    <div>
                      <span className="font-mono text-sm">{job.cronExpression}</span>
                      <span className="ml-2 text-gray-500 text-sm">
                        ({job.timezone || 'UTC'})
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-500">Manual only</span>
                  )}
                </DetailRow>
                <DetailRow label="Output Format">
                  {job.outputFormat.toUpperCase()}
                </DetailRow>
                <DetailRow label="Compression">
                  {job.compression.toUpperCase()}
                </DetailRow>
                <DetailRow label="Target Database">
                  {targetDatabase ? (
                    <span className="font-mono text-sm">
                      {targetDatabase.managedSqlDbName} ({targetDatabase.managedSqlDbCode})
                    </span>
                  ) : job.managedSqlDbId ? (
                    <span className="text-gray-500">{job.managedSqlDbId}</span>
                  ) : (
                    <span className="text-gray-400">Not specified</span>
                  )}
                </DetailRow>
              </dl>
            </div>

            {/* Queries */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Queries ({job.queries.filter((q) => q.isActive).length} active / {job.queries.length} total)
              </h3>
              <div className="space-y-3">
                {job.queries.map((query, index) => (
                  <div
                    key={query.queryId}
                    className={`border rounded-lg p-3 ${
                      query.isActive ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900">
                        {index + 1}. {query.queryName}
                      </span>
                      <div className="flex items-center gap-2">
                        {query.landingConfig && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                            Landing: {query.landingConfig.schemaName ? `${query.landingConfig.schemaName}.` : ''}{query.landingConfig.tableName}
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            query.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {query.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <pre className="text-xs bg-gray-100 p-2 rounded overflow-x-auto">
                      {query.sqlQuery}
                    </pre>
                    {query.landingConfig && (
                      <div className="mt-2 text-xs text-gray-500">
                        <span className="font-medium">Cleanup:</span> {query.landingConfig.cleanupStrategy.strategy}
                        {query.landingConfig.autoCreateTable && ' | Auto-create table'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Parameters */}
            {job.parameters && job.parameters.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Parameters</h3>
                <div className="space-y-2">
                  {job.parameters.map((param) => (
                    <div key={param.name} className="text-sm">
                      <span className="font-medium">{param.name}:</span>{' '}
                      <span className="text-gray-600">{param.defaultValue}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pipeline Configuration */}
            {job.pipelineConfig?.enabled && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Pipeline Configuration</h3>
                <dl className="border-t border-gray-200">
                  <DetailRow label="Status">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Enabled
                    </span>
                  </DetailRow>
                  <DetailRow label="Batch Size">{job.pipelineConfig.loadBatchSize} rows</DetailRow>
                  <DetailRow label="Error Handling">{job.pipelineConfig.errorHandling}</DetailRow>
                  <DetailRow label="Staging Steps">
                    {job.pipelineConfig.stagingSteps.length} step(s)
                  </DetailRow>
                  <DetailRow label="Production Steps">
                    {job.pipelineConfig.productionSteps.length} step(s)
                  </DetailRow>
                </dl>
              </div>
            )}

            {/* Timestamps */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Timestamps</h3>
              <dl className="border-t border-gray-200">
                <DetailRow label="Created">{formatDate(job.createdAt)}</DetailRow>
                <DetailRow label="Last Updated">{formatDate(job.updatedAt)}</DetailRow>
              </dl>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t">
              <Button variant="secondary" onClick={onClose}>
                Close
              </Button>
              <Button
                variant="primary"
                onClick={() => onTrigger(job)}
                disabled={!job.isActive}
              >
                Run Now
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};
